// Guided Builder node schema for the FIS Prepaid ACC hierarchy.
// Separate from accRecipes.js (Quick Edit) — reuses its serializer + validators.
// Each node def declares: element name, allowed child types, ordered field
// sections (drive the form), and build(values) -> ordered scalar rows.

import {
  buildBatch, renderItem, validateField, fieldVisible, PURSE_NAME_RE,
} from './accRecipes';

export { buildBatch, renderItem };

// ---- shared option lists ----
const PA_HIER = [
  { code: '1', label: 'Add (1)' },
  { code: '3', label: 'Overwrite (3)' },
];
const splitCode = (s) => String(s || '').split(' · ')[0];
const isAdd = (v) => v.__pa === '1';
const isUpd = (v) => v.__pa === '3';

// ---- field shorthands ----
const num = (id, label, extra = {}) => ({ id, label, type: 'number', ...extra });
const bit = (id, label, dflt = '', extra = {}) => ({ id, label, type: 'select', options: ['', '1', '0'], default: dflt, ...extra });

// ====================================================================
export const NODE_DEFS = {
  // ---------------------------------------------------- CLIENT
  client: {
    element: 'Client', label: 'Client', icon: 'Building2', order: 0,
    children: ['program'], pa: PA_HIER, defaultPa: '1',
    sections: [
      { title: 'Identity', fields: [
        { id: 'ParentName', label: 'Parent Name', type: 'text', maxLen: 30, showIf: isAdd, help: 'Where to attach (Business Partner for level 2).' },
        { id: 'ClientName', label: 'Client Name', type: 'text', maxLen: 30, required: true },
        { id: 'NewClientName', label: 'New Client Name', type: 'text', maxLen: 30, showIf: isUpd, help: 'Rename (must not already exist).' },
        { id: 'TreeLevel', label: 'Tree Level', type: 'select', options: ['', '2', '3', '4', '5', '6', '7', '8', '9'], required: isAdd, showIf: isAdd },
      ] },
      { title: 'Alternate ID', fields: [
        { id: 'ClientSpecificID', label: 'Client Specific ID', type: 'text', maxLen: 26, help: 'PPDB / CES / custom.' },
        { id: 'UniqueLevel', label: 'Unique Level', type: 'select', options: ['', 'Top', 'Parent', 'None'] },
      ] },
    ],
    build: (v) => [
      ['PerformAction', v.__pa], ['ParentName', v.ParentName], ['ClientName', v.ClientName],
      ['NewClientName', v.NewClientName], ['TreeLevel', v.TreeLevel],
      ['ClientSpecificID', v.ClientSpecificID], ['UniqueLevel', v.UniqueLevel],
    ],
  },

  // ---------------------------------------------------- PROGRAM
  program: {
    element: 'Program', label: 'Program', icon: 'FolderTree', order: 1,
    children: ['sub_program'], pa: PA_HIER, defaultPa: '1',
    sections: [
      { title: 'Identity', fields: [
        { id: 'ClientName', label: 'Client Name', type: 'text', maxLen: 30, help: 'Parent client (optional when nested).' },
        { id: 'ProgramName', label: 'Program Name', type: 'text', maxLen: 30, required: true },
        { id: 'NewProgramName', label: 'New Program Name', type: 'text', maxLen: 30, showIf: isUpd },
        { id: 'ReportName', label: 'Report Name', type: 'text', maxLen: 30, required: isAdd },
      ] },
      { title: 'Dates', fields: [
        { id: 'EffectiveDate', label: 'Effective Date', type: 'date', required: isAdd },
        { id: 'LastIssueDate', label: 'Last Issue Date', type: 'date', required: isAdd },
        { id: 'StartDate', label: 'Start Date', type: 'date', required: isAdd },
        { id: 'EndDate', label: 'End Date', type: 'date', required: isAdd },
        { id: 'CloseOutDate', label: 'Close-Out Date', type: 'date', required: isAdd },
      ] },
    ],
    build: (v) => [
      ['PerformAction', v.__pa], ['ClientName', v.ClientName], ['ProgramName', v.ProgramName],
      ['NewProgramName', v.NewProgramName], ['ReportName', v.ReportName],
      ['EffectiveDate', v.EffectiveDate], ['LastIssueDate', v.LastIssueDate], ['StartDate', v.StartDate],
      ['EndDate', v.EndDate], ['CloseOutDate', v.CloseOutDate],
    ],
  },

  // ---------------------------------------------------- SUB_PROGRAM
  sub_program: {
    element: 'Sub_Program', label: 'Sub_Program', icon: 'Wallet', order: 2,
    children: ['highRiskMcc', 'fudgeMcc', 'feeSetup', 'packageProgram', 'defaultPurse', 'daSetup', 'purse'], pa: PA_HIER, defaultPa: '1',
    // Sections mirror the FIS "Subprogram Attribute" (SubAr) report so the read-back
    // and the builder line up. Field ids = ACC XML element names (Appendix B).
    sections: [
      { title: 'Identity & strategy', fields: [
        { id: 'ProgramName', label: 'Program Name', type: 'text', maxLen: 30, required: true },
        { id: 'Sub_ProgramName', label: 'Sub_Program Name', type: 'text', maxLen: 30, required: true },
        { id: 'NewSub_ProgramName', label: 'New Sub_Program Name', type: 'text', maxLen: 30, showIf: isUpd },
        { id: 'ReportName', label: 'Report Name', type: 'text', maxLen: 30, required: isAdd },
        { id: 'AuthStrategy', label: 'Auth Strategy', type: 'select', options: ['VULTURE', 'HAWK'], default: 'HAWK', required: true, help: 'HAWK adds healthcare (IIAS) checks.' },
        { id: 'IIASMCCGroup', label: 'IIAS MCC Group', type: 'text', pattern: /^\d{5}$/, patmsg: '5-digit', required: (v) => v.AuthStrategy === 'HAWK', showIf: (v) => v.AuthStrategy === 'HAWK' },
      ] },
      { title: 'General program info', fields: [
        { id: 'MarketSegment', label: 'Market Segment', type: 'select', default: '4 · Insurance',
          options: ['4 · Insurance', '1 · Tax Refund', '2 · Payroll', '3 · Incentive', '6 · VTM', '7 · Gift', '8 · Promo', '9 · Mall', '12 · Other'] },
        { id: 'CardType', label: 'Card Type', type: 'select', default: '1 · Normal',
          options: ['1 · Normal', '2 · Emergency Relief', '8 · VTM', '13 · Gift', '14 · Money Transfer'] },
        num('AKAtype', 'AKA Type', { help: 'Proxy handling (domain default applies if blank).' }),
        num('ProxyType', 'Proxy Type'),
        bit('PrintProxy', 'Print Proxy'),
        bit('FISLiability', 'FIS Assumes Fraud Liability', '', { help: 'Default 0 (client owns liability); usually cannot be changed.' }),
      ] },
      { title: 'Authorization', fields: [
        { id: 'AuthenticateMethod', label: 'IVR Authenticate Method', type: 'select', default: '1 · Last 4 SSN',
          options: ['0 · None', '1 · Last 4 SSN', '2 · Last 4 Card#', '3 · CVX2', '4 · Last 4 Phone', '5 · First 5 Zip', '7 · DDMM Birthday', '8 · MMDD Birthday', '9 · Pseudo', '14 · PIN'] },
        num('MaxPinRetry', 'Max PIN Retry', { help: 'Default 2.' }),
        bit('SplitPurse', 'Split Purse', '', { help: 'Allow one purchase to draw from multiple purses.' }),
        { id: 'ImmediateCredit', label: 'Immediate Credit', type: 'select', options: ['', '0 · Off', '1 · Auth FS/APL', '2 · Settlement'] },
        { id: 'ExpiredPurseActionID', label: 'Expired Purse Action', type: 'select', options: ['', '1 · Credit current active purse', '2 · Extend expired purse'] },
        num('ExpiredPurseExtensionDays', 'Expired Purse Extension (days)', { min: 15, max: 45, showIf: (v) => String(v.ExpiredPurseActionID).startsWith('2') }),
        bit('IsDelegatedAuthEnabled', 'Delegated Authorization'),
        bit('IsCVV2RequiredForEcom', 'Require CVV2 for e-commerce'),
      ] },
      { title: 'Card parameters', fields: [
        bit('Active', 'Active', '1'),
        { id: 'Personalized', label: 'Program Type', type: 'select', options: ['1 · Personalized', '0 · Non-personalized'], default: '1 · Personalized' },
        bit('RePlastic', 'Re-Plastic', '1'),
        bit('LoadSuspend', 'Load Suspend', '1'),
        bit('Restrictadjust', 'Restrict Adjust'),
        bit('Reloadable', 'Reloadable', '1'),
        { id: 'InitialStatus', label: 'Initial Card Status', type: 'select', options: ['', '1 · Ready', '0 · Dormant', '2 · Active'], default: '1 · Ready' },
        { id: 'ActiveMethod', label: 'Active Method', type: 'select', options: ['', '0 · Individual', '1 · Group'] },
      ] },
      { title: 'PIN handling', fields: [
        bit('PinBased', 'PIN Based'),
        { id: 'HandlePin', label: 'IVR/MyAccount PIN options', type: 'select', showIf: (v) => v.PinBased === '1',
          options: ['', '0 · No action', '3 · IVR hear & change', '12 · Web see & change', '13 · Web see & change, IVR hear', '14 · Web see & change, IVR change', '15 · Web see & change, IVR hear & change'] },
        { id: 'PinMethod', label: 'PIN Method', type: 'select', showIf: (v) => v.PinBased === '1',
          options: ['', '0 · None', '1 · Card Carrier', '2 · PIN mailer', '3 · PIN mailer on request'] },
        bit('PinMailer', 'Suppress PIN Mailer', '', { showIf: (v) => v.PinBased === '1', help: '1 = do NOT send PIN mailers.' }),
        num('PinMailerlag', 'PIN Mailer Lag (days)', { showIf: (v) => v.PinBased === '1' }),
        num('PinLockExpiration', 'Days PIN Locked', { min: 0, max: 365, showIf: (v) => v.PinBased === '1' }),
      ] },
      { title: 'Card expiration', fields: [
        { id: 'PhysicalExpireMethod', label: 'Physical Expiration', type: 'select', options: ['', '2 · Dynamic', '0 · None', '1 · Static', '3 · Linked'], default: '2 · Dynamic' },
        num('PhysicalExpireMonths', 'Static/Dynamic Entry (months)', { help: 'Must be a multiple of 12.' }),
        bit('AdvanceExpire', 'Advance Expire'),
        num('RandomExpirationDateRangeInMonths', 'Random Expiration Range (months)', { min: 0, max: 6 }),
      ] },
      { title: 'Auto renewal', fields: [
        bit('AutoRenew', 'Auto Renewal'),
        num('AutoRenewWindow', 'Renew Window (days before expiry)', { min: 1, max: 60, showIf: (v) => v.AutoRenew === '1' }),
        num('AutoRenewMonths', 'Renew Months (new card life)', { min: 1, max: 60, showIf: (v) => v.AutoRenew === '1' }),
        bit('AutoRenewal', 'Auto Renewal (card flag)', '', { showIf: (v) => v.AutoRenew === '1', help: 'Should mirror Auto Renewal.' }),
      ] },
      { title: 'Card production', fields: [
        { id: 'PackageName', label: 'Package Name', type: 'text', maxLen: 80, required: isAdd },
        { id: 'IssuingBank', label: 'Issuing Bank', type: 'text', maxLen: 23, required: isAdd },
        { id: 'BinNumber', label: 'BIN Number', type: 'text', pattern: /^\d{6}$/, patmsg: '6 digits', required: isAdd },
        { id: 'FulfillmentHouse', label: 'Fulfillment House', type: 'text', maxLen: 40, help: 'Exact fulfillment-house name as configured at FIS.' },
        num('TotalNumberOfCards', 'Total Number of Cards', { min: 1, max: 800000 }),
        { id: 'PrinNumber', label: 'PRIN Number', type: 'text', pattern: /^\d{1,3}$/, patmsg: '0–999' },
      ] },
      { title: 'Embossing & fulfillment', fields: [
        bit('EmbossExpireDate', 'Emboss/Print Expire Date', '1'),
        bit('EmbossBeginDate', 'Emboss/Print Begin Date'),
        { id: 'StaticFourth', label: 'Fourth Line (static)', type: 'text', maxLen: 26, help: 'Text on the 4th embossed line. Use “.” to clear.' },
        { id: 'ReturnAddress1', label: 'Return Address 1', type: 'text', maxLen: 26 },
        { id: 'ReturnAddress2', label: 'Return Address 2', type: 'text', maxLen: 26 },
        { id: 'ReturnAddress3', label: 'Return Address 3', type: 'text', maxLen: 26 },
        { id: 'ReturnAddress4', label: 'Return Address 4', type: 'text', maxLen: 26 },
        bit('IssueDuplicateCard', 'Issue Duplicate Card'),
        bit('EmbDiscretionaryData1', 'Discretionary Data 1', '1'),
        bit('EmbDiscretionaryData2', 'Discretionary Data 2', '1'),
        bit('EmbDiscretionaryData3', 'Discretionary Data 3', '1'),
      ] },
      { title: 'Servicing & disputes', fields: [
        bit('RegE', 'Reg E', '1'),
        bit('DisputeServices', 'Dispute Services', '1'),
        bit('TempCredits', 'Temp Credits'),
        bit('SettlementServices', 'Settlement Services'),
        bit('NegBalMgmt', 'Negative Balance Management'),
      ] },
    ],
    build: (v) => {
      const sc = splitCode;
      return [
        ['PerformAction', v.__pa], ['ProgramName', v.ProgramName], ['Sub_ProgramName', v.Sub_ProgramName],
        ['NewSub_ProgramName', v.NewSub_ProgramName], ['ReportName', v.ReportName],
        ['MarketSegment', sc(v.MarketSegment)], ['CardType', sc(v.CardType)], ['AuthenticateMethod', sc(v.AuthenticateMethod)],
        ['Active', v.Active], ['Personalized', sc(v.Personalized)], ['RePlastic', v.RePlastic], ['LoadSuspend', v.LoadSuspend],
        ['Restrictadjust', v.Restrictadjust], ['InitialStatus', sc(v.InitialStatus)], ['ActiveMethod', sc(v.ActiveMethod)],
        ['PhysicalExpireMethod', sc(v.PhysicalExpireMethod)], ['PhysicalExpireMonths', v.PhysicalExpireMonths],
        ['StaticFourth', v.StaticFourth], ['EmbossBeginDate', v.EmbossBeginDate], ['EmbossExpireDate', v.EmbossExpireDate],
        ['ReturnAddress1', v.ReturnAddress1], ['ReturnAddress2', v.ReturnAddress2], ['ReturnAddress3', v.ReturnAddress3], ['ReturnAddress4', v.ReturnAddress4],
        ['IssueDuplicateCard', v.IssueDuplicateCard],
        ['PackageName', v.PackageName], ['TotalNumberOfCards', v.TotalNumberOfCards], ['IssuingBank', v.IssuingBank],
        ['BinNumber', v.BinNumber], ['FulfillmentHouse', v.FulfillmentHouse],
        ['MaxPinRetry', v.MaxPinRetry], ['SplitPurse', v.SplitPurse],
        ['IIASMCCGroup', v.AuthStrategy === 'HAWK' ? v.IIASMCCGroup : ''],
        ['AutoRenew', v.AutoRenew], ['AutoRenewWindow', v.AutoRenew === '1' ? v.AutoRenewWindow : ''],
        ['AutoRenewMonths', v.AutoRenew === '1' ? v.AutoRenewMonths : ''], ['AdvanceExpire', v.AdvanceExpire],
        ['AuthStrategy', v.AuthStrategy],
        ['TempCredits', v.TempCredits], ['RegE', v.RegE], ['SettlementServices', v.SettlementServices], ['DisputeServices', v.DisputeServices],
        ['Reloadable', v.Reloadable], ['NegBalMgmt', v.NegBalMgmt], ['FISLiability', v.FISLiability],
        ['AKAtype', v.AKAtype], ['ProxyType', v.ProxyType], ['PrintProxy', v.PrintProxy],
        ['EmbDiscretionaryData1', v.EmbDiscretionaryData1], ['EmbDiscretionaryData2', v.EmbDiscretionaryData2], ['EmbDiscretionaryData3', v.EmbDiscretionaryData3],
        ['PinBased', v.PinBased],
        ['HandlePin', v.PinBased === '1' ? sc(v.HandlePin) : ''], ['PinLockExpiration', v.PinBased === '1' ? v.PinLockExpiration : ''],
        ['PinMethod', v.PinBased === '1' ? sc(v.PinMethod) : ''], ['PinMailerlag', v.PinBased === '1' ? v.PinMailerlag : ''], ['PinMailer', v.PinBased === '1' ? v.PinMailer : ''],
        ['IsCVV2RequiredForEcom', v.IsCVV2RequiredForEcom], ['ImmediateCredit', sc(v.ImmediateCredit)],
        ['ExpiredPurseActionID', sc(v.ExpiredPurseActionID)], ['ExpiredPurseExtensionDays', String(v.ExpiredPurseActionID).startsWith('2') ? v.ExpiredPurseExtensionDays : ''],
        ['RandomExpirationDateRangeInMonths', v.RandomExpirationDateRangeInMonths],
        ['AutoRenewal', v.AutoRenew === '1' ? v.AutoRenewal : ''],
        ['IsDelegatedAuthEnabled', v.IsDelegatedAuthEnabled], ['PrinNumber', v.PrinNumber],
      ];
    },
  },

  // ---------------------------------------------------- PURSE (wrapped in <Purses>)
  purse: {
    element: 'Purse', label: 'Purse', icon: 'Coins', order: 5, wrapper: 'Purses',
    children: ['purseCashRule'], pa: PA_HIER, defaultPa: '1',
    sections: [
      { title: 'Identity', fields: [
        { id: 'Sub_ProgramName', label: 'Sub_Program Name', type: 'text', maxLen: 30, help: 'Optional when nested.' },
        { id: 'PurseName', label: 'Purse Name', type: 'text', pattern: PURSE_NAME_RE, patmsg: '3 letters + 4 digits, e.g. FSA2026', required: true },
        { id: 'NewPurseName', label: 'New Purse Name', type: 'text', pattern: PURSE_NAME_RE, patmsg: '3 letters + 4 digits', showIf: isUpd, help: 'Rename an existing purse.' },
        { id: 'PurseDescription', label: 'Purse Description', type: 'text', maxLen: 30, required: isAdd },
        num('PurseSlot', 'Purse Slot'),
        { id: 'PursePriorityList', label: 'Purse Priority List', type: 'text', maxLen: 2048, help: 'e.g. FSA,HSA,OTC' },
      ] },
      { title: 'Plan period', fields: [
        { id: 'EffectiveDate', label: 'Effective Date', type: 'date', required: isAdd },
        { id: 'ExpDate', label: 'Expiry Date', type: 'date', required: isAdd },
        { id: 'ExpExtensionFlag', label: 'Grace / Extension', type: 'select', options: ['No Grace Period', 'POS and Batch', 'POS Only', 'Batch Only'], default: 'No Grace Period' },
        num('ExtensionPeriodinDays', 'Extension Period (days)', { min: 1, max: 80, required: (v) => v.ExpExtensionFlag !== 'No Grace Period', showIf: (v) => v.ExpExtensionFlag !== 'No Grace Period' }),
        { id: 'ExtensionDate', label: 'Extension Date', type: 'date', required: (v) => v.ExpExtensionFlag !== 'No Grace Period', showIf: (v) => v.ExpExtensionFlag !== 'No Grace Period' },
      ] },
      { title: 'Rules & limits', fields: [
        { id: 'MCCGroup', label: 'MCC Group', type: 'text', pattern: /^\d{5}$/, patmsg: '5-digit', required: isAdd, help: 'The allowed MCC category group (purse strategy = MCC).' },
        { id: 'NetworkName', label: 'Network Name', type: 'text', maxLen: 30, help: 'Allowed merchant network, if used instead of/with the MCC group.' },
        { id: 'IIASGroup', label: 'IIAS / Filtered-Spend Group', type: 'text', pattern: /^\d{5}$/, patmsg: '5-digit' },
        { id: 'PurseHandle', label: 'Purse Handle', type: 'select', options: ['', 'HSA Purse'], help: 'Domain 12 — export this purse to a trustee (e.g. HSA).' },
        num('MinValue', 'Min Value'), num('MaxValue', 'Max Value'),
        num('MinLoad', 'Min Load'), num('MaxLoad', 'Max Load'),
      ] },
      { title: 'Transfers & flags', fields: [
        bit('NoFeeFlag', 'No-Fee Flag', '', { help: 'Domain 3 — 1 = purse rejects fees.' }),
        bit('TransferOutFlag', 'OTB Transfer Out'),
        num('MaxTransferAmount', 'Max Transfer Amount', { showIf: (v) => v.TransferOutFlag === '1', required: (v) => v.TransferOutFlag === '1' }),
        num('DaysToMonitor', 'Days to Monitor', { min: 0, max: 180, showIf: (v) => v.TransferOutFlag === '1', required: (v) => v.TransferOutFlag === '1' }),
        bit('EnforcePurseName', 'Enforce Purse Name'),
        bit('PreventPurseOverlap', 'Prevent Purse Overlap'),
        bit('EligibleForMFASweep', 'Eligible for MFA Sweep', '', { help: 'Filtered Spend programs only.' }),
      ] },
    ],
    build: (v) => [
      ['PerformAction', v.__pa], ['Sub_ProgramName', v.Sub_ProgramName], ['PurseName', v.PurseName],
      ['NewPurseName', v.__pa === '3' ? v.NewPurseName : ''],
      ['PurseSlot', v.PurseSlot], ['PurseDescription', v.PurseDescription], ['PursePriorityList', v.PursePriorityList],
      ['EffectiveDate', v.EffectiveDate], ['ExpDate', v.ExpDate],
      ['ExpExtensionFlag', v.ExpExtensionFlag === 'No Grace Period' ? '' : v.ExpExtensionFlag],
      ['ExtensionPeriodinDays', v.ExpExtensionFlag === 'No Grace Period' ? '' : v.ExtensionPeriodinDays],
      ['ExtensionDate', v.ExpExtensionFlag === 'No Grace Period' ? '' : v.ExtensionDate],
      ['NetworkName', v.NetworkName], ['MCCGroup', v.MCCGroup],
      ['NoFeeFlag', v.NoFeeFlag], ['TransferOutFlag', v.TransferOutFlag],
      ['MaxTransferAmount', v.TransferOutFlag === '1' ? v.MaxTransferAmount : ''],
      ['DaysToMonitor', v.TransferOutFlag === '1' ? v.DaysToMonitor : ''],
      ['IIASGroup', v.IIASGroup], ['PurseHandle', v.PurseHandle],
      ['MinValue', v.MinValue], ['MaxValue', v.MaxValue], ['MinLoad', v.MinLoad], ['MaxLoad', v.MaxLoad],
      ['EnforcePurseName', v.EnforcePurseName], ['PreventPurseOverlap', v.PreventPurseOverlap],
      ['EligibleForMFASweep', v.EligibleForMFASweep],
    ],
  },

  // ---------------------------------------------------- HIGH-RISK MCC (nested in Sub_Program)
  highRiskMcc: {
    element: 'HighRiskMCCs', label: 'High-Risk MCC', icon: 'ShieldAlert', order: 3,
    children: [], pa: [{ code: '1', label: 'Add (1)' }, { code: '3', label: 'Update (3)' }], defaultPa: '1',
    sections: [
      { title: 'Rule', fields: [
        { id: 'Sub_ProgramName', label: 'Sub_Program Name', type: 'text', maxLen: 30, help: 'Optional when nested.' },
        { id: 'Action', label: 'Action', type: 'select', options: ['Add', 'Delete'], default: 'Add' },
        { id: 'HighRiskMCC', label: 'MCC', type: 'text', pattern: /^\d{4}$/, patmsg: '4 digits', required: true },
      ] },
    ],
    build: (v) => [
      ['PerformAction', v.__pa], ['Sub_ProgramName', v.Sub_ProgramName], ['Action', v.Action], ['HighRiskMCC', v.HighRiskMCC],
    ],
  },

  // ---------------------------------------------------- FEE SETUP (nested in Sub_Program)
  feeSetup: {
    element: 'FeeSetup', label: 'Fee Setup', icon: 'BadgeDollarSign', order: 4,
    children: [], pa: [{ code: '1', label: 'Add (1)' }, { code: '3', label: 'Update (3)' }], defaultPa: '1',
    sections: [
      { title: 'Fee', fields: [
        { id: 'Sub_ProgramName', label: 'Sub_Program Name', type: 'text', maxLen: 30, help: 'Optional when nested.' },
        { id: 'Action', label: 'Action', type: 'select', options: ['Add', 'Update', 'Delete'], default: 'Add' },
        { id: 'FeeName', label: 'Fee', type: 'select', options: ['Card Replacement Fee', 'Card Reissue Fee'], default: 'Card Replacement Fee' },
        num('FeeAmt', 'Fee Amount', { min: 0, max: 19.99, required: true }),
      ] },
    ],
    build: (v) => [
      ['PerformAction', v.__pa], ['Sub_ProgramName', v.Sub_ProgramName], ['Action', v.Action], ['FeeName', v.FeeName], ['FeeAmt', v.FeeAmt],
    ],
  },

  // ---------------------------------------------------- FUDGE MCC (nested in Sub_Program)
  fudgeMcc: {
    element: 'FudgeMCCs', label: 'Fudge MCC', icon: 'ShieldAlert', order: 3.2,
    children: [], pa: [{ code: '1', label: 'Add (1)' }, { code: '3', label: 'Update (3)' }], defaultPa: '1',
    sections: [{ title: 'Tolerance', fields: [
      { id: 'Sub_ProgramName', label: 'Sub_Program Name', type: 'text', maxLen: 30, help: 'Optional when nested.' },
      { id: 'Action', label: 'Action', type: 'select', options: ['Add', 'Delete'], default: 'Add' },
      { id: 'FudgeMCC', label: 'Fudge MCC', type: 'text', pattern: /^\d{4}$/, patmsg: '4 digits', required: true },
      num('FudgeLimit', 'Fudge Limit', { min: 0, max: 99.99, required: true }),
      { id: 'FudgeType', label: 'Fudge Type', type: 'select', options: ['Amount', 'Percentage'], default: 'Amount' },
      { id: 'FudgeSource', label: 'Fudge Source', type: 'select', options: ['', 'ITLVIS', 'MCV', 'MastercardDebit', 'DSV', 'EFD', 'CUP', 'Visa Base 1', 'Visa EU'] },
    ] }],
    build: (v) => [
      ['PerformAction', v.__pa], ['Sub_ProgramName', v.Sub_ProgramName], ['Action', v.Action],
      ['FudgeMCC', v.FudgeMCC], ['FudgeLimit', v.FudgeLimit], ['FudgeType', v.FudgeType], ['FudgeSource', v.FudgeSource],
    ],
  },

  // ---------------------------------------------------- PACKAGE PROGRAM (nested in Sub_Program)
  packageProgram: {
    element: 'PackageProgram', label: 'Package Program', icon: 'CreditCard', order: 6, wrapper: 'PackagePrograms',
    children: [], pa: [{ code: '1', label: 'Add (1)' }, { code: '2', label: 'Update (2)' }], defaultPa: '1',
    sections: [{ title: 'Package / BIN', fields: [
      { id: 'Sub_ProgramName', label: 'Sub_Program Name', type: 'text', maxLen: 30, help: 'Optional when nested.' },
      { id: 'PackageName', label: 'Package Name', type: 'text', maxLen: 30, help: 'Provide name OR ID.' },
      num('PackageID', 'Package ID'),
      { id: 'BinNumber', label: 'BIN Number', type: 'text', pattern: /^\d{1,6}$/, patmsg: 'up to 6 digits', required: true },
      num('TotalNumberOfCards', 'Total Number of Cards', { min: 1, max: 800000, required: true }),
      { id: 'PRINNumber', label: 'PRIN Number', type: 'text', pattern: /^\d{1,3}$/, patmsg: '0–999' },
    ] }],
    build: (v) => [
      ['PerformAction', v.__pa], ['Sub_ProgramName', v.Sub_ProgramName], ['PackageName', v.PackageName],
      ['PackageID', v.PackageID], ['BinNumber', v.BinNumber], ['TotalNumberOfCards', v.TotalNumberOfCards], ['PRINNumber', v.PRINNumber],
    ],
  },

  // ---------------------------------------------------- DEFAULT PURSE (nested in Sub_Program)
  defaultPurse: {
    element: 'DefaultPurse', label: 'Default Purse', icon: 'Coins', order: 6.5,
    children: [], pa: [{ code: '1', label: 'Set (1)' }, { code: '3', label: 'Update (3)' }], defaultPa: '1',
    sections: [{ title: 'Default purse', fields: [
      { id: 'Sub_ProgramName', label: 'Sub_Program Name', type: 'text', maxLen: 30, help: 'Optional when nested.' },
      { id: 'DefaultPurseName', label: 'Default Purse Name', type: 'text', pattern: PURSE_NAME_RE, patmsg: '3 letters + 4 digits' },
      num('DefaultPurseSlot', 'Default Purse Slot'),
    ] }],
    build: (v) => [
      ['PerformAction', v.__pa], ['Sub_ProgramName', v.Sub_ProgramName], ['DefaultPurseSlot', v.DefaultPurseSlot], ['DefaultPurseName', v.DefaultPurseName],
    ],
  },

  // ---------------------------------------------------- DA SETUP (nested in Sub_Program)
  daSetup: {
    element: 'DASetup', label: 'Direct Access (ACH)', icon: 'Banknote', order: 7,
    children: [], pa: PA_HIER, defaultPa: '1',
    sections: [{ title: 'Direct Access', fields: [
      { id: 'Sub_ProgramName', label: 'Sub_Program Name', type: 'text', maxLen: 30, help: 'Must follow a purse creation.' },
      { id: 'BankPrefix', label: 'Bank Prefix', type: 'text', pattern: /^\d{1,3}$/, patmsg: 'up to 3 digits, no leading zero', required: isAdd },
      { id: 'TRN', label: 'Transit Routing #', type: 'text', pattern: /^\d{9}$/, patmsg: '9 digits', required: isAdd },
      { id: 'Active', label: 'Active', type: 'select', options: ['', '1', '0'] },
      { id: 'Withdrawal', label: 'Withdrawal', type: 'select', options: ['', 'Yes', 'No', 'Restricted'] },
      { id: 'DefaultDAPurseName', label: 'Default DA Purse Name', type: 'text', pattern: PURSE_NAME_RE, patmsg: '3 letters + 4 digits' },
      num('DefaultDAPurseSlot', 'Default DA Purse Slot'),
    ] }],
    build: (v) => [
      ['PerformAction', v.__pa], ['Sub_ProgramName', v.Sub_ProgramName], ['BankPrefix', v.BankPrefix], ['TRN', v.TRN],
      ['Active', v.Active], ['Withdrawal', v.Withdrawal], ['DefaultDAPurseSlot', v.DefaultDAPurseSlot], ['DefaultDAPurseName', v.DefaultDAPurseName],
    ],
  },

  // ---------------------------------------------------- PURSE CASH RULE (nested in Purse)
  purseCashRule: {
    element: 'PurseCashRules', label: 'Cash / MCC Track', icon: 'Coins', order: 1,
    children: [], pa: [{ code: '1', label: 'Add (1)' }, { code: '5', label: 'Delete (5)' }], defaultPa: '1',
    sections: [{ title: 'Track', fields: [
      { id: 'Sub_ProgramName', label: 'Sub_Program Name', type: 'text', maxLen: 30, help: 'Optional when nested.' },
      { id: 'PurseName', label: 'Purse Name', type: 'text', pattern: PURSE_NAME_RE, patmsg: '3 letters + 4 digits', help: 'Optional when nested.' },
      { id: 'Action', label: 'Action', type: 'select', options: ['Add', 'Delete'], default: 'Add' },
      { id: 'kind', label: 'Track type', type: 'select', options: ['Cash track', 'MCC track'], default: 'Cash track' },
      { id: 'CashType', label: 'Cash Type', type: 'select', showIf: (v) => v.kind === 'Cash track', required: (v) => v.kind === 'Cash track',
        options: ['General purchase', 'Financial Institutions :Manual Cash Disbursements', 'Financial Institutions :Automated Cash Disbursements', 'Financial Institutions :International ATM', 'DDA transfer', 'Cashback'], default: 'General purchase' },
      { id: 'MCCGroup', label: 'MCC Group', type: 'text', showIf: (v) => v.kind === 'MCC track', required: (v) => v.kind === 'MCC track', pattern: /^\d{5}$/, patmsg: '5-digit' },
      { id: 'CycleType', label: 'Cycle Type', type: 'select', options: ['Monthly', 'Daily'], default: 'Monthly', showIf: (v) => v.kind === 'Cash track' },
      num('CycleReset', 'Cycle Reset', { min: 0, max: 999, showIf: (v) => v.kind === 'Cash track' }),
      num('CycleSpendMaxAmount', 'Cycle Spend Max', { showIf: (v) => v.kind === 'Cash track' }),
      num('TxnSpendMinAmount', 'Txn Min Amount'),
      num('TxnSpendMaxAmount', 'Txn Max Amount'),
    ] }],
    build: (v) => [
      ['PerformAction', v.Action === 'Delete' ? '5' : '1'], ['Sub_ProgramName', v.Sub_ProgramName], ['PurseName', v.PurseName], ['Action', v.Action],
      ['CashType', v.kind === 'Cash track' ? v.CashType : ''], ['MCCGroup', v.kind === 'MCC track' ? v.MCCGroup : ''],
      ['CycleType', v.kind === 'Cash track' ? v.CycleType : ''], ['CycleReset', v.kind === 'Cash track' ? v.CycleReset : ''],
      ['CycleSpendMaxAmount', v.kind === 'Cash track' ? v.CycleSpendMaxAmount : ''],
      ['TxnSpendMinAmount', v.TxnSpendMinAmount], ['TxnSpendMaxAmount', v.TxnSpendMaxAmount],
    ],
  },

  // ---------------------------------------------------- PACKAGE (root / standalone)
  package: {
    element: 'Package', label: 'Package', icon: 'CreditCard', order: 9,
    children: [], pa: [{ code: '2', label: 'Rename (2)' }, { code: '1', label: 'Create (1)' }], defaultPa: '2',
    sections: [{ title: 'Package', fields: [
      { id: 'OldPackageName', label: 'Old Package Name', type: 'text', maxLen: 80, required: (v) => v.__pa === '2', showIf: (v) => v.__pa === '2' },
      { id: 'PackageName', label: 'Package Name', type: 'text', maxLen: 80, required: true },
      { id: 'BankName', label: 'Bank Name', type: 'text', maxLen: 23, required: (v) => v.__pa === '1', showIf: (v) => v.__pa === '1' },
      { id: 'FormFactorID', label: 'Form Factor', type: 'select', showIf: (v) => v.__pa === '1',
        options: ['', '1 · Mag Stripe Visa/MC', '2 · Contact Chip + Mag', '8 · Mag Stripe Discover', '9 · MC Contactless + Mag'] },
      { id: 'ArtworkName', label: 'Artwork Name', type: 'text', maxLen: 40, default: 'Zurich Benny Card', showIf: (v) => v.__pa === '1' },
    ] }],
    build: (v) => (v.__pa === '1'
      ? [['PerformAction', '1'], ['PackageName', v.PackageName], ['BankName', v.BankName], ['FormFactorID', splitCode(v.FormFactorID)], ['ArtworkName', v.ArtworkName]]
      : [['PerformAction', '2'], ['OldPackageName', v.OldPackageName], ['PackageName', v.PackageName]]),
  },

  // ---------------------------------------------------- CONFIGURE FEE (root / standalone)
  configureFee: {
    element: 'Configure_Fee', label: 'Configure Fee', icon: 'BadgeDollarSign', order: 8,
    children: [], pa: [{ code: '2', label: 'Modify (2)' }], defaultPa: '2',
    sections: [{ title: 'Fee', fields: [
      { id: 'Sub_ProgramName', label: 'Sub_Program Name', type: 'text', maxLen: 30, required: true },
      { id: 'FeeName', label: 'Fee', type: 'select', options: ['Card Replacement Fee', 'Card Reissue Fee'], default: 'Card Replacement Fee' },
      num('FeeAmt', 'Fee Amount', { min: 0, max: 19.99, required: true }),
    ] }],
    build: (v) => [['PerformAction', '2'], ['Sub_ProgramName', v.Sub_ProgramName], ['FeeName', v.FeeName], ['FeeAmt', v.FeeAmt]],
  },

  // ---------------------------------------------------- BLOCKED COUNTRY (root, wrapped)
  blockedCountry: {
    element: 'BlockedCountry', label: 'Blocked Country', icon: 'Globe', order: 11, wrapper: 'BlockedCountries',
    children: [], pa: [{ code: '1', label: 'Block (1)' }, { code: '3', label: 'Unblock (3)' }], defaultPa: '1',
    sections: [{ title: 'Country', fields: [
      { id: 'Sub_ProgramName', label: 'Sub_Program Name', type: 'text', maxLen: 30, required: true },
      { id: 'CountryCode', label: 'Country Code', type: 'text', pattern: /^\d{1,3}$/, patmsg: '0–999', required: true },
      { id: 'ResponseDescription', label: 'Response', type: 'select', options: ['Service Restricted', 'Decline', 'Approval'], default: 'Service Restricted' },
    ] }],
    build: (v) => [['PerformAction', v.__pa], ['Sub_ProgramName', v.Sub_ProgramName], ['CountryCode', v.CountryCode], ['ResponseDescription', v.ResponseDescription]],
  },

  // ---------------------------------------------------- ADDRESS CHANGE VELOCITY (root)
  addressVelocity: {
    element: 'AddressChangeVelocity', label: 'Address Velocity', icon: 'Globe', order: 12,
    children: [], pa: [{ code: '1', label: 'Add (1)' }, { code: '3', label: 'Remove (3)' }], defaultPa: '1',
    sections: [{ title: 'Address change', fields: [
      { id: 'Sub_ProgramName', label: 'Sub_Program Name', type: 'text', maxLen: 30, required: true },
      num('AddressFreezeOnActivation', 'Freeze on Activation (days)', { min: 0, max: 999 }),
      num('AddressChangeInterval', 'Change Interval (days)', { min: 0, max: 999 }),
      num('MaxAddressChanges', 'Max Changes / interval', { min: 0, max: 999 }),
      bit('ConfirmReplaceDoc', 'Confirm Replace Doc'),
      bit('ConfirmReissueDoc', 'Confirm Reissue Doc'),
    ] }],
    build: (v) => [
      ['PerformAction', v.__pa], ['Sub_ProgramName', v.Sub_ProgramName], ['AddressFreezeOnActivation', v.AddressFreezeOnActivation],
      ['AddressChangeInterval', v.AddressChangeInterval], ['MaxAddressChanges', v.MaxAddressChanges],
      ['ConfirmReplaceDoc', v.ConfirmReplaceDoc], ['ConfirmReissueDoc', v.ConfirmReissueDoc],
    ],
  },

  // ---------------------------------------------------- SUBPROGRAM PACKAGE LINK (root, wrapped)
  spPackageLink: {
    element: 'SubprogramPackageLink', label: 'Package Link', icon: 'CreditCard', order: 13, wrapper: 'SubprogramPackageLinks',
    children: [], pa: [{ code: '1', label: 'Add (1)' }], defaultPa: '1',
    sections: [{ title: 'BIN / PRIN', fields: [
      { id: 'Sub_ProgramName', label: 'Sub_Program Name', type: 'text', maxLen: 30, required: true },
      { id: 'PackageName', label: 'Package Name', type: 'text', maxLen: 30, help: 'Provide name OR ID.' },
      num('PackageID', 'Package ID'),
      { id: 'BIN', label: 'BIN', type: 'text', pattern: /^\d{1,8}$/, patmsg: 'up to 8 digits', required: true },
      { id: 'PRIN', label: 'PRIN', type: 'text', pattern: /^\d{1,3}$/, patmsg: '0–999', required: true },
    ] }],
    build: (v) => [
      ['PerformAction', v.__pa], ['Sub_ProgramName', v.Sub_ProgramName], ['PackageName', v.PackageName], ['PackageID', v.PackageID], ['BIN', v.BIN], ['PRIN', v.PRIN],
    ],
  },

  // ---------------------------------------------------- SUBPROGRAM PACKAGE BPA LINK (root, wrapped)
  spPackageBpaLink: {
    element: 'SubprogramPackageBPALink', label: 'Package BPA Link', icon: 'CreditCard', order: 14, wrapper: 'SubprogramPackageBPALinks',
    children: [], pa: [{ code: '1', label: 'Add (1)' }], defaultPa: '1',
    sections: [{ title: 'BIN / PRIN / Agent', fields: [
      { id: 'Sub_ProgramName', label: 'Sub_Program Name', type: 'text', maxLen: 30, required: true },
      { id: 'PackageName', label: 'Package Name', type: 'text', maxLen: 30, help: 'Provide name OR ID.' },
      num('PackageID', 'Package ID'),
      { id: 'BIN', label: 'BIN', type: 'text', pattern: /^\d{1,8}$/, patmsg: 'up to 8 digits', required: true },
      { id: 'PRIN', label: 'PRIN', type: 'text', pattern: /^\d{1,3}$/, patmsg: '0–999', required: true },
      num('BeginAgent', 'Begin Agent', { min: 0, max: 99, required: true }),
      num('EndAgent', 'End Agent', { min: 0, max: 99, required: true }),
    ] }],
    build: (v) => [
      ['PerformAction', v.__pa], ['Sub_ProgramName', v.Sub_ProgramName], ['PackageName', v.PackageName], ['PackageID', v.PackageID],
      ['BIN', v.BIN], ['PRIN', v.PRIN], ['BeginAgent', v.BeginAgent], ['EndAgent', v.EndAgent],
    ],
  },

  // ---------------------------------------------------- PURSE PRIORITY SET (root)
  pursePrioritySet: {
    element: 'PursePrioritySets', label: 'Purse Priority Set', icon: 'Coins', order: 6.8,
    children: [], pa: [{ code: '1', label: 'Add (1)' }, { code: '3', label: 'Update (3)' }], defaultPa: '1',
    sections: [{ title: 'Priority set', fields: [
      { id: 'Sub_ProgramName', label: 'Sub_Program Name', type: 'text', maxLen: 30, required: true },
      { id: 'PursePrioritySetName', label: 'Priority Set Name', type: 'text', maxLen: 50, required: true },
      { id: 'PursePriorityOrder', label: 'Purse Priority Order', type: 'text', required: true, help: 'Purse types in order, comma-separated. e.g. OTP,OCP,OGP,CFO,DOT' },
    ] }],
    build: (v) => [
      ['PerformAction', v.__pa], ['Sub_ProgramName', v.Sub_ProgramName],
      ['PursePrioritySetName', v.PursePrioritySetName],
      ['PursePriorityOrder', String(v.PursePriorityOrder || '').replace(/\s+/g, '')],
    ],
  },
};

export const ROOT_TYPES = ['client', 'program', 'sub_program', 'purse', 'package', 'configureFee', 'blockedCountry', 'addressVelocity', 'spPackageLink', 'spPackageBpaLink', 'pursePrioritySet'];

// ---- helpers ----
export function flattenFields(type) {
  return NODE_DEFS[type].sections.flatMap((s) => s.fields);
}

let _seq = 0;
export function makeNode(type) {
  const def = NODE_DEFS[type];
  const values = { __pa: def.defaultPa || '1' };
  for (const f of flattenFields(type)) values[f.id] = f.default ?? '';
  _seq += 1;
  return { id: `${type}_${_seq}`, type, values, children: [], included: true };
}

export function validateNode(node) {
  const errors = {};
  for (const f of flattenFields(node.type)) {
    const e = validateField(f, node.values[f.id], node.values);
    if (e) errors[f.id] = e;
  }
  return errors;
}

export function nodeFieldVisible(field, values) {
  return fieldVisible(field, values);
}

// Recursively count validation errors across the tree (skips excluded nodes).
export function treeErrorCount(nodes) {
  let n = 0;
  const walk = (list) => list.forEach((nd) => {
    if (nd.included === false) return;
    n += Object.keys(validateNode(nd)).length;
    walk(nd.children);
  });
  walk(nodes);
  return n;
}

// Assemble a list of sibling nodes into serializer rows: excluded nodes are
// skipped, siblings ordered by def.order, and same-wrapper siblings grouped
// into one wrapper element (e.g. many <Purse> -> one <Purses>).
function assembleRows(nodes) {
  const ordered = nodes
    .filter((n) => n.included !== false)
    .sort((a, b) => (NODE_DEFS[a.type].order || 0) - (NODE_DEFS[b.type].order || 0));
  const direct = [];
  const wrapGroups = {};
  for (const child of ordered) {
    const cdef = NODE_DEFS[child.type];
    const item = buildTreeItem(child);
    if (cdef.wrapper) (wrapGroups[cdef.wrapper] = wrapGroups[cdef.wrapper] || []).push(item);
    else direct.push(item);
  }
  const wrapRows = Object.entries(wrapGroups).map(([w, items]) => ({ node: w, rows: items }));
  return [...direct, ...wrapRows];
}

// Build one tree node -> serializer item (scalar rows + nested/wrapped children).
export function buildTreeItem(node) {
  const def = NODE_DEFS[node.type];
  return { node: def.element, rows: [...def.build(node.values), ...assembleRows(node.children)] };
}

// Build the batch-level item list (root nodes, with root-level wrapper grouping).
export function buildTreeRoots(roots) {
  return assembleRows(roots);
}

// ---- import: parse an existing batch XML back into the tree ----
const REVERSE_ELEMENT = {
  Client: 'client', Program: 'program', Sub_Program: 'sub_program', Purse: 'purse',
  HighRiskMCCs: 'highRiskMcc', FudgeMCCs: 'fudgeMcc', FeeSetup: 'feeSetup',
  PackageProgram: 'packageProgram', DefaultPurse: 'defaultPurse', DASetup: 'daSetup', PurseCashRules: 'purseCashRule',
  BlockedCountry: 'blockedCountry', AddressChangeVelocity: 'addressVelocity',
  SubprogramPackageLink: 'spPackageLink', SubprogramPackageBPALink: 'spPackageBpaLink',
  Package: 'package', Configure_Fee: 'configureFee', PursePrioritySets: 'pursePrioritySet',
};
const WRAPPER_ELEMENTS = new Set([
  'Purses', 'BlockedCountries', 'SubprogramPackageLinks', 'SubprogramPackageBPALinks', 'PackagePrograms',
]);
const IGNORE_ELEMENTS = new Set(['Response']);

function displayValueFor(field, raw) {
  if (!field || field.type !== 'select' || !Array.isArray(field.options)) return raw;
  if (field.options.includes(raw)) return raw;
  const coded = field.options.find((o) => o.startsWith(`${raw} · `));
  return coded || raw;
}

function elementToNode(el) {
  const type = REVERSE_ELEMENT[el.tagName];
  const node = makeNode(type);
  const fields = flattenFields(type);
  for (const c of Array.from(el.children)) {
    const tag = c.tagName;
    if (tag === 'PerformAction') { node.values.__pa = c.textContent.trim(); continue; }
    if (WRAPPER_ELEMENTS.has(tag) || REVERSE_ELEMENT[tag] || IGNORE_ELEMENTS.has(tag)) continue;
    const field = fields.find((f) => f.id === tag);
    if (field) node.values[tag] = displayValueFor(field, c.textContent.trim());
  }
  node.children = parseNodeList(el);
  if (type === 'purseCashRule' && node.values.MCCGroup) node.values.kind = 'MCC track';
  return node;
}

function parseNodeList(parentEl) {
  const out = [];
  for (const el of Array.from(parentEl.children)) {
    if (WRAPPER_ELEMENTS.has(el.tagName)) out.push(...parseNodeList(el));
    else if (REVERSE_ELEMENT[el.tagName]) out.push(elementToNode(el));
  }
  return out;
}

export function parseBatch(xmlString) {
  const doc = new DOMParser().parseFromString(xmlString, 'application/xml');
  if (doc.querySelector('parsererror')) throw new Error('Invalid XML — check for syntax errors.');
  const batch = doc.documentElement;
  if (!batch || batch.tagName !== 'Batch') throw new Error('Root element must be <Batch>.');
  const businessPartner = batch.getAttribute('BusinessPartner') || '';
  const roots = parseNodeList(batch);
  if (roots.length === 0) throw new Error('No recognized ACC nodes found in the batch.');
  return { businessPartner, roots };
}
